
import { Component, Watch } from 'vue-property-decorator';
import VWrapper from '@/components/shared/VWrapper.vue';
import { namespace } from 'vuex-class';
import { Venue } from '@/interfaces/models/Venue';
import { Table as TableModel } from '@/interfaces/models/Table';
import { Payment as PaymentModel } from '@/interfaces/models/Payment';
import Notification from '@/mixins/Notification';
import TableTable from '@/components/table/TableTable.vue';
import VEmptyState from '@/components/shared/VEmptyState.vue';
import { mixins } from 'vue-class-component';
import Filter from '@/interfaces/api/Filter';
import PaymentTable from '@/components/payment/PaymentTable.vue';
import { Order } from '@/interfaces/models/Order';
import OrderServiceApi from '@/api/http/OrderServiceApi';
import VRangePicker from '@/components/shared/form/VRangePicker.vue';
import _ from 'lodash';
import PaymentApiService from '@/api/http/PaymentApiService';
import { ValidationObserver } from 'vee-validate';
import { toUrl } from '@/util/helper';

const venue = namespace('venue');
const payment = namespace('payment');

@Component({
  components: { PaymentTable, VEmptyState, TableTable, VWrapper, VRangePicker },
})
export default class Payment extends mixins(Notification) {
  @venue.State('active') public venue!: Venue;

  @payment.State('items') public payments!: PaymentModel[];
  @payment.State((state) => state.pagination.total) public total!: number;
  @payment.Action('fetch') public getPayments!: any;
  @payment.State('filter') public filter!: Filter;
  @payment.Action('setFilter') public setFilter!: (filter: Filter) => void;
  @payment.Action('refund') public refund!: any;

  public code: string = '';
  public transactionId: string = '';
  public invoiceModalVisible: boolean = false;
  public invoiceModalData: PaymentModel | null = null;
  public invoiceEmail: string = '';

  public $refs!: {
    table: InstanceType<typeof PaymentTable> & { getSelected: () => TableModel[] };
    observer: InstanceType<typeof ValidationObserver>;
  };

  public orderMap: Map<string, Order> = new Map<string, Order>();

  public orders: Order[] = [];

  public debouncedSearchByCode = _.debounce((self: any) => {
    if ((!self.code && self.transactionId) || !self.venue?._id) {
      return;
    }

    const filter: Filter = { ...self.filter, venue: self.venue._id };

    if (!self.code) {
      delete filter.code;
      self.fetchPayments(filter);
      return;
    }

    filter.code = self.code;
    delete filter.transactionId;
    self.transactionId = '';
    self.fetchPayments(filter);
  }, 500);

  public debouncedSearchByTransactionId = _.debounce((self: any) => {
    if ((!self.transactionId && self.code) || !self.venue?._id) {
      return;
    }

    const filter: Filter = { ...self.filter, venue: self.venue._id };

    if (!self.transactionId) {
      delete filter.transactionId;
      self.fetchPayments(filter);
      return;
    }

    filter.transactionId = self.transactionId;
    delete filter.code;
    self.code = '';
    self.fetchPayments(filter);
  }, 500);

  public onChange(data: any): void {
    const { sortBy, sortDesc, page, itemsPerPage } = data;
    const direction: string = sortDesc?.[0] ? 'desc' : 'asc';
    let limit: number = itemsPerPage || 0;
    let pageNumber: number = page;

    if (itemsPerPage === -1) {
      limit = 0;
      pageNumber = 0;
    }

    const filter = {
      ...this.filter,
      direction,
      sort: sortBy[0],
      limit,
    };

    this.fetchPayments(filter, pageNumber);
  }

  public created() {
    if (!this.venue) {
      this.$startLoading('venue');
    }
  }

  public fetchPayments(filter: Filter, page?: number, limit?: number) {
    this.$startLoading('payment');
    this.setFilter(filter);
    this.getPayments({ page: page || 1 });
    this.$stopLoading('payment');
  }

  public async doRefund(data: { order: string; articles: string[] }) {
    await this.refund(data);
    await this.getPayments({ page: 1, limit: this.filter?.limit || 10 });
  }

  public async getOrders(item: PaymentModel) {
    this.$startLoading('order');
    const api: OrderServiceApi = new OrderServiceApi();
    for (const order of item.orders) {
      if (!this.orderMap.has((order as Partial<Order>)._id as string)) {
        this.orderMap.set(
          (order as Partial<Order>)._id as string,
          (await api.show({ id: (order as Partial<Order>)._id as string })).data,
        );
      }
    }
    this.orders = Array.from(this.orderMap.values());
    this.$stopLoading('order');
  }

  public mounted() {
    if (!this.venue) {
      this.$startLoading('venue');
      return;
    }

    const filter: Filter = { ...this.filter, venue: this.venue._id };
    this.fetchPayments(filter);
  }

  @Watch('code')
  public onCodeSearch() {
    this.debouncedSearchByCode(this);
  }

  @Watch('transactionId')
  public onTransactionIdSearch() {
    this.debouncedSearchByTransactionId(this);
  }

  public onChangeRange(data: any): void {
    const filter: Filter = { ...this.filter, ...data };
    this.fetchPayments(filter);
  }

  public openSendInvoiceModal(item: PaymentModel) {
    this.invoiceModalVisible = true;
    this.invoiceModalData = item;
  }

  public sendInvoice() {
    this.$refs.observer.validate().then((res) => {
      if (!res) return;
      if (!this.invoiceModalData) return;
      const api: PaymentApiService = new PaymentApiService();
      api.sendInvoiceReceipt({ payment: this.invoiceModalData._id, email: this.invoiceEmail }).then(() => {
        this.notifySuccess('payment.notification.invoiceSent');
        this.closeSendInvoiceModal();
      });
    });
  }

  public closeSendInvoiceModal() {
    this.invoiceModalVisible = false;
    this.invoiceModalData = null;
    this.invoiceEmail = '';
  }

  public exportCsv() {
    const api: PaymentApiService = new PaymentApiService();
    api.exportPaymentCsv(this.filter).then((response) => {
      if (!response?.data) {
        return;
      }

      const datesNameString =
        this.filter.from === this.filter.to ? this.filter.from : `${this.filter.from}_to_${this.filter.to}`;

      toUrl(response.data, `payments_${datesNameString}.csv`);
    });
  }

  @Watch('venue')
  public onVenueChange() {
    if (this.venue && this.venue._id) {
      const filter: Filter = { ...this.filter, venue: this.venue._id };
      this.fetchPayments(filter);
    }
  }
}
